import {
    Decimal,
    TrackGetResponse,
    Ulid,
} from "@kase/my-marathon-communication";
import { store } from "~/core/store";
import { dateToApi, upsert } from "~/utils";
import { ApiSliceParams } from "../interfaces";
import { createEmitter, createListController } from "../utils";
import {
    ReportRequest,
    ReportTrackRequest,
    Track,
    UpdateTrackRequest,
} from "./interfaces";
import { getRangeKey, getRelation, getReportStart } from "./utils";

const ONE_MINUTE = 1000 * 60;

export const makeTrack = ({ connection, company }: ApiSliceParams) => {
    const { emit, on, off } = createEmitter<Track>();

    const view = async (
        track: TrackGetResponse,
        { start, end }: { start: Date; end: Date },
    ) => {
        const { task: taskApi, time: timeApi } = store.api;
        const finalTrack: Track = {
            ...track,
            fee_code:
                track.fee_code && Ulid.ZERO.equals(track.fee_code)
                    ? undefined
                    : track.fee_code,
            lob:
                track.lob && Ulid.ZERO.equals(track.lob)
                    ? undefined
                    : track.lob,
            _id: track.id,
            id: track.id.toString(),
        };

        if (finalTrack.kind?.$case === "task") {
            try {
                finalTrack.task = await taskApi.getTask(finalTrack.kind.task);
            } catch (error: unknown) {
                console.log("[track.view] cannot load task", error);
            }
        }

        if (finalTrack.kind?.$case === "track_group") {
            try {
                finalTrack.group =
                    (await timeApi.group.getOrLoad(
                        finalTrack.kind.track_group,
                    )) ?? undefined;
            } catch (error: unknown) {
                console.log("[track.view] cannot load group", error);
            }
        }

        if (finalTrack.kind?.$case === "calendar_external") {
            try {
                const ext =
                    end.getTime() === 0
                        ? await timeApi.external.getOrForceLoad(
                              finalTrack.kind.calendar_external,
                              { start, end },
                          )
                        : await timeApi.external.getOrLoad(
                              finalTrack.kind.calendar_external,
                              { start, end },
                          );

                finalTrack.external = ext ?? undefined;
            } catch (error: unknown) {
                console.log("[track.view] cannot load external", error);
            }
        }

        return finalTrack;
    };

    const {
        items: tracks,
        loading,
        loaded,
        listen,
    } = createListController<
        TrackGetResponse,
        Track,
        { start: Date; end: Date }
    >({
        name: "track",
        connection,
        getKey: ({ start, end }) => getRangeKey(start, end),
        view,
        list: (con, { start, end }) =>
            con.time.track.items({
                start: BigInt(start.getTime()),
                stop: BigInt(end.getTime()),
            }),
        emit,
    });

    const loadTrack = async (
        trackId: Readonly<Ulid> | readonly Readonly<Ulid>[],
    ) => {
        const con = connection.value;
        const meId = con.user.user_id ? Ulid.fromLoHi(con.user.user_id) : null;
        const finalId = Array.isArray(trackId) ? trackId : [trackId];
        const result: Track[] = [];
        const list = con.time.track.get({ id: finalId });

        for await (const item of list) {
            // this forces the track to be loaded only if it belongs to the current user
            //
            if (!meId || !meId.equals(item.user)) {
                continue;
            }

            // @TODO: temporary hardcoded start and end
            // loadTrack function is only used when manipulating with tasks which are not fully supported yet
            // startTask and stopTrack should use watch function (which should be implemented in create-list-controller) instead of loading by id
            //
            const start = new Date();
            const end = new Date();

            start.setDate(1);
            end.setMonth(end.getMonth() + 1);
            end.setDate(0);

            const finalTrack = await view(item, { start, end });

            result.push(finalTrack);
        }

        if (result.length) {
            tracks.value = upsert(tracks.peek().slice(), result, "id");
            emit("add", result);
        }

        return result;
    };

    const remove = async (id: Readonly<Ulid>) => {
        const con = connection.value;
        await con.time.track.delete({ id });
    };

    const startTask = async (task: Readonly<Ulid>) => {
        const con = connection.value;
        const id = await con.time.track.create({
            kind: { $case: "task", task },
        });
        const result = await loadTrack(id);

        return result[0];
    };

    const startGroup = (group: Readonly<Ulid>) => {
        const con = connection.value;
        return con.time.track.create({
            kind: { $case: "track_group", track_group: group },
        });
    };

    const startExternal = (external: Readonly<Ulid>) => {
        const con = connection.value;
        return con.time.track.create({
            kind: {
                $case: "calendar_external",
                calendar_external: external,
            },
        });
    };

    const stopTrack = async (id: Readonly<Ulid>) => {
        const con = connection.value;
        const track = tracks.peek().find((t) => t._id.equals(id));
        const now = Date.now();

        if (track && now - track.start < ONE_MINUTE) {
            await con.time.track.delete({
                id,
            });

            return track;
        }

        const resultId = await con.time.track.stop({ id });
        const result = await loadTrack(resultId);

        return result[0];
    };

    // @TODO: think about moving this function into report slice
    const reportTrack = async ({
        id,
        comment,
        start,
        end,
        feeCode,
        feeCategory,
        project,
    }: ReportTrackRequest) => {
        const track = tracks.peek().find((t) => t._id.equals(id));
        const task = track?.task;
        const group = track?.group;
        const external = track?.external;

        if (!track || track.stop === 0 || !(task || group || external)) {
            throw new Error(
                "Track has not been stopped or it is not attached to a task or group or external",
            );
        }

        const diff = end.getTime() - start.getTime();
        const hours = diff / 1000 / 60 / 60;

        return report({
            date: start,
            comment,
            hours,
            feeCode: feeCode ?? task?._feeCode ?? group?.feeCode?._id,
            feeCategory,
            project: project ?? task?._project ?? group?.project?._id,
            relation: {
                trackId: track._id,
            },
        });
    };

    // @TODO: think about moving this function into report slice
    const report = async ({
        date,
        comment,
        hours,
        feeCode,
        feeCategory,
        project,
        relation,
        onlyDuration,
    }: ReportRequest) => {
        const con = connection.value;
        const lob =
            feeCategory && Ulid.ZERO.equals(feeCategory)
                ? undefined
                : feeCategory;

        return con.time.create({
            company: company.value,
            date: dateToApi(date),
            description: comment,
            project,
            relation: getRelation(relation),
            start: getReportStart(date, onlyDuration),
            line_type: {
                $case: "fee",
                fee: {
                    feecode: feeCode,
                    hours: new Decimal(hours),
                    lob,
                },
            },
        });
    };

    const update = async ({
        id,
        start,
        end,
        feeCode,
        feeCategory,
        comment,
    }: UpdateTrackRequest) => {
        const con = connection.value;
        await con.time.track.update({
            id,
            start: BigInt(start.getTime()),
            stop: end ? BigInt(end.getTime()) : undefined,
            description: comment,
            fee_code: feeCode ?? Ulid.ZERO,
            lob: feeCategory ?? Ulid.ZERO,
            company: company.value,
        });
    };

    const get = (id: Readonly<Ulid> | null | undefined) => {
        if (!id) {
            return null;
        }

        return tracks.peek().find((t) => t._id.equals(id)) ?? null;
    };

    const listenActive = () => listen({ start: new Date(0), end: new Date(0) });

    return {
        tracks,
        loading,
        loaded,
        listen,
        listenActive,
        startTask,
        startGroup,
        startExternal,
        stopTrack,
        reportTrack,
        report,
        remove,
        update,
        on,
        off,
        get,
    };
};
